import type { SVGProps } from 'react';

// TS port of the source (Falah) app's components/icons.jsx, merged with the
// target's pre-existing icon set (IconView/IconEdit/IconTrash/IconRestore —
// kept, deduplicated against the source's own IconView/IconEdit/IconTrash).
// IconSingpass is deliberately dropped: Singpass was removed from the
// backend in an earlier phase of this migration and no page in this pass
// (or any planned later pass) renders it.
const base = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.7,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

export function IconMenu(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base} {...props}>
      <path d="M3.5 6.5h17M3.5 12h17M3.5 17.5h17" />
    </svg>
  );
}

export function IconDashboard(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base} {...props}>
      <rect x="3.5" y="3.5" width="7" height="8" rx="1.4" />
      <rect x="13.5" y="3.5" width="7" height="5" rx="1.4" />
      <rect x="13.5" y="11.5" width="7" height="9" rx="1.4" />
      <rect x="3.5" y="14.5" width="7" height="6" rx="1.4" />
    </svg>
  );
}

export function IconMembers(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base} {...props}>
      <circle cx="9" cy="8" r="3.2" />
      <path d="M3.5 20c0-3.6 2.5-6 5.5-6s5.5 2.4 5.5 6" />
      <circle cx="17" cy="8.5" r="2.4" />
      <path d="M15.2 12.2c2.3.3 4.3 2.4 4.3 5.6" />
    </svg>
  );
}

export function IconEvents(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base} {...props}>
      <rect x="3.5" y="5" width="17" height="15.5" rx="1.8" />
      <path d="M3.5 9.5h17" />
      <path d="M8 3v3.5M16 3v3.5" />
      <circle cx="12" cy="14.5" r="1.4" fill="currentColor" stroke="none" />
    </svg>
  );
}

// A simple suitcase — used as the Leave marker on the Home calendar,
// distinct from IconEvents' calendar-page glyph.
export function IconLeave(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base} {...props}>
      <rect x="3.5" y="8" width="17" height="12" rx="1.8" />
      <path d="M8.5 8V6a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v2" />
      <path d="M3.5 13h17" />
    </svg>
  );
}

export function IconAnnouncements(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base} {...props}>
      <path d="M4 11v3a1.6 1.6 0 0 0 1.6 1.6H7l3.6 4V5.4L7 9.4H5.6A1.6 1.6 0 0 0 4 11Z" />
      <path d="M13.5 8.3a5 5 0 0 1 0 7.9" />
      <path d="M16.2 5.8a9 9 0 0 1 0 12.9" />
    </svg>
  );
}

export function IconFinance(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base} {...props}>
      <circle cx="12" cy="12" r="8.3" />
      <path d="M12 6.7v10.6" />
      <path d="M14.7 9.3c0-1.1-1.2-2-2.7-2s-2.7.9-2.7 2 1.2 1.7 2.7 2 2.7.9 2.7 2-1.2 2-2.7 2-2.7-.9-2.7-2" />
    </svg>
  );
}

export function IconProcurement(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base} {...props}>
      <path d="M3.5 4h2.2l1 3M6.7 7l1.7 8.2a1.6 1.6 0 0 0 1.6 1.3h6.8a1.6 1.6 0 0 0 1.6-1.3L20 7H6.7Z" />
      <circle cx="10" cy="20" r="1.2" fill="currentColor" stroke="none" />
      <circle cx="16.5" cy="20" r="1.2" fill="currentColor" stroke="none" />
    </svg>
  );
}

export function IconOperations(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base} {...props}>
      <path d="M3.5 10.3 12 4l8.5 6.3" />
      <path d="M5 9.2V20h14V9.2" />
      <path d="M9.5 20v-6.2h5V20" />
    </svg>
  );
}

export function IconHR(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base} {...props}>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

export function IconInvestments(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base} {...props}>
      <path d="M3 17l6-6 4 4 8-8" />
      <path d="M17 7h4v4" />
      <path d="M3 21h18" />
    </svg>
  );
}

export function IconSystem(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base} {...props}>
      <circle cx="12" cy="12" r="6.2" />
      <circle cx="12" cy="12" r="2.3" />
      {[0, 45, 90, 135, 180, 225, 270, 315].map((angle) => (
        <rect
          key={angle}
          x="10.6"
          y="1.4"
          width="2.8"
          height="3"
          rx="0.8"
          fill="currentColor"
          stroke="none"
          transform={`rotate(${angle} 12 12)`}
        />
      ))}
    </svg>
  );
}

export function IconReports(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base} {...props}>
      <path d="M6 3.5h9.2L19 7.3V20a1.4 1.4 0 0 1-1.4 1.4H6A1.4 1.4 0 0 1 4.6 20V4.9A1.4 1.4 0 0 1 6 3.5Z" />
      <path d="M14.8 3.5V7a1 1 0 0 0 1 1h3.2" />
      <path d="M8 13.5v4M12 11v6.5M16 15v2.5" />
    </svg>
  );
}

export function IconBack(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base} {...props}>
      <path d="M11 5 4 12l7 7" />
      <path d="M4 12h16" />
    </svg>
  );
}

export function IconDownload(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base} {...props}>
      <path d="M12 3.5v11.7" />
      <path d="M7.3 10.7 12 15.4l4.7-4.7" />
      <path d="M4.5 17.5v1.8a1.7 1.7 0 0 0 1.7 1.7h11.6a1.7 1.7 0 0 0 1.7-1.7v-1.8" />
    </svg>
  );
}

export function IconEdit(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base} {...props}>
      <path d="M15.7 4.3a1.8 1.8 0 0 1 2.5 0l1.5 1.5a1.8 1.8 0 0 1 0 2.5L8.5 19.5 4 20.5l1-4.5Z" />
      <path d="M14 6l4 4" />
    </svg>
  );
}

export function IconTrash(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base} {...props}>
      <path d="M4.5 7h15" />
      <path d="M9 7V4.8A1.3 1.3 0 0 1 10.3 3.5h3.4A1.3 1.3 0 0 1 15 4.8V7" />
      <path d="M6.5 7 7.4 19.2A1.6 1.6 0 0 0 9 20.7h6a1.6 1.6 0 0 0 1.6-1.5L17.5 7" />
      <path d="M10.2 11v6M13.8 11v6" />
    </svg>
  );
}

export function IconRestore(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base} {...props}>
      <path d="M3 12a9 9 0 1 0 3-6.7" />
      <path d="M3 3v6h6" />
    </svg>
  );
}

export function IconAttach(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base} {...props}>
      <path d="M16.3 6.6 8.9 14a2.6 2.6 0 0 0 3.7 3.7l7.9-7.9a4.4 4.4 0 1 0-6.2-6.2l-8 8a6.2 6.2 0 0 0 8.8 8.8l7-7" />
    </svg>
  );
}

export function IconInbox(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base} {...props}>
      <path d="M4 12.5h4.3l1.4 2.6h4.6l1.4-2.6H20" />
      <path d="M5.3 6 4 12.5v5A1.6 1.6 0 0 0 5.6 19h12.8a1.6 1.6 0 0 0 1.6-1.6v-5L18.7 6a1.6 1.6 0 0 0-1.5-1H6.8a1.6 1.6 0 0 0-1.5 1Z" />
    </svg>
  );
}

export function IconView(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base} {...props}>
      <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" />
      <circle cx="12" cy="12" r="2.8" />
    </svg>
  );
}

export function IconGoogle(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" {...props}>
      <path
        fill="#4285F4"
        d="M23.5 12.3c0-.85-.08-1.66-.22-2.44H12v4.62h6.46c-.28 1.5-1.13 2.78-2.4 3.63v3h3.88c2.27-2.09 3.56-5.17 3.56-8.81Z"
      />
      <path
        fill="#34A853"
        d="M12 24c3.24 0 5.96-1.07 7.94-2.9l-3.88-3c-1.08.72-2.45 1.15-4.06 1.15-3.12 0-5.77-2.11-6.71-4.94H1.28v3.1A12 12 0 0 0 12 24Z"
      />
      <path
        fill="#FBBC05"
        d="M5.29 14.31A7.2 7.2 0 0 1 4.91 12c0-.8.14-1.58.38-2.31v-3.1H1.28A12 12 0 0 0 0 12c0 1.94.46 3.77 1.28 5.4l4.01-3.09Z"
      />
      <path
        fill="#EA4335"
        d="M12 4.75c1.76 0 3.35.61 4.6 1.79l3.44-3.44C17.95 1.19 15.24 0 12 0A12 12 0 0 0 1.28 6.6l4.01 3.1c.94-2.84 3.59-4.95 6.71-4.95Z"
      />
    </svg>
  );
}

export function IconMicrosoft(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" {...props}>
      <rect x="1" y="1" width="10.2" height="10.2" fill="#F25022" />
      <rect x="12.8" y="1" width="10.2" height="10.2" fill="#7FBA00" />
      <rect x="1" y="12.8" width="10.2" height="10.2" fill="#00A4EF" />
      <rect x="12.8" y="12.8" width="10.2" height="10.2" fill="#FFB900" />
    </svg>
  );
}

export function IconCheck(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base} {...props}>
      <path d="M4.5 12.5 9.5 17.5 19.5 6.5" />
    </svg>
  );
}

export function IconClockOut(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base} {...props}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5v4.8l3.2 2" />
    </svg>
  );
}

export function IconClose(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base} {...props}>
      <path d="M6 6 18 18M18 6 6 18" />
    </svg>
  );
}

export function IconChevronDown(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base} {...props}>
      <path d="M6 9.5 12 15l6-5.5" />
    </svg>
  );
}

export function IconAlertTriangle(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base} {...props}>
      <path d="M12 4.5 21 19.5H3L12 4.5Z" />
      <path d="M12 10v4" />
      <circle cx="12" cy="16.7" r="0.6" fill="currentColor" stroke="none" />
    </svg>
  );
}

export function IconHelp(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base} {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M9.2 9.3a2.8 2.8 0 1 1 4.3 2.4c-.9.6-1.5 1.1-1.5 2.1" />
      <circle cx="12" cy="17" r="0.6" fill="currentColor" stroke="none" />
    </svg>
  );
}

export function IconHistory(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...base} {...props}>
      <path d="M4 4.5V9h4.5" />
      <path d="M4.6 15A8.4 8.4 0 1 0 6 7.6L4 9.5" />
      <path d="M12 8v4.3l3 2" />
    </svg>
  );
}
